import { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { createAvatar } from "@dicebear/core";
import { identicon } from "@dicebear/collection";

/**
 * The signed-in account, assembled the one way both /me and /me/settings need
 * it.
 *
 * Cookies first, the server record layered on top once it answers: the cookies
 * hold everything the header renders, so the page never flashes blank, but only
 * the record has the profile picture and the join date, and only it is right
 * after another device changed something.
 *
 * This lives in a hook rather than in each page because that duplication is
 * what /me was created to end. MyProfile and MyBleeps each carried their own
 * copy of this fetch and their own spelling of the anonymous-alias rule, and
 * the two had already drifted: one trusted `u.anonymous ?? u.isAnonymous`, the
 * other only the cookie.
 *
 * `reload()` is for after a modal saves. Both change modals write a cookie and
 * stop, so the page has to pull the value back through itself: refreshUserData
 * re-reads the cookies App.js holds, and the key bump refetches the record.
 */
export const useAccount = (userData, refreshUserData) => {
  const userId = userData?.[0] ?? Cookies.get("userId");
  const userType = userData?.[1] ?? Cookies.get("userType");
  const userName = userData?.[2] ?? Cookies.get("userName");
  const cookieEmail = userData?.[3] ?? Cookies.get("userEmail");
  const cookieAnonymous = userData?.[7] ?? Cookies.get("userAnonymous");

  /* An account with no type is a regular user, which is how App.js reads the
     same cookie when it decides between the homepage and the dashboard. */
  const isStaff = !!userType && userType !== "user";

  const [profile, setProfile] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    fetch(`/api/users/${userId}`, { method: "GET", redirect: "follow" })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data && typeof data === "object") setProfile(data);
      })
      .catch((err) => console.error("Failed to load user:", err));

    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  const reload = useCallback(() => {
    refreshUserData?.();
    setReloadKey((key) => key + 1);
  }, [refreshUserData]);

  const isAnonymous = profile
    ? profile.anonymous === true
    : cookieAnonymous === true || cookieAnonymous === "true";

  const displayName =
    isAnonymous && userId
      ? `Bleeper#${String(userId).slice(-6)}`
      : profile?.username || userName || "Your name";

  const email = profile?.email || cookieEmail || "";

  const joined = useMemo(() => {
    const raw = profile?.dateJoined;
    if (!raw) return "";
    const date = new Date(raw);
    return isNaN(date)
      ? ""
      : date.toLocaleString("en-US", { month: "long", year: "numeric" });
  }, [profile]);

  /* Same seed and the same generator as the navbar, so the two avatars match. */
  const identiconUrl = useMemo(
    () =>
      createAvatar(identicon, {
        seed: displayName || String(userId || "bleep-user"),
        backgroundType: ["gradientLinear", "solid"],
      }).toDataUri(),
    [displayName, userId],
  );

  const hasPhoto = typeof profile?.pfp === "string" && profile.pfp.trim() !== "";

  return {
    userId,
    userType,
    isStaff,
    isReporter: !isStaff,
    profile,
    displayName,
    email,
    isAnonymous,
    joined,
    hasPhoto,
    avatarSrc: hasPhoto ? profile.pfp.trim() : identiconUrl,
    reload,
  };
};
